/**
 * Utility functions for handling repeaterKeywords synchronization
 * Specifically for Q8 Company Ownership data transformation
 */

import { findFieldById, FieldConfigEntry } from './fieldConfigUtils';
import type { RepeaterEntry, RepeaterRow } from '../types/domain';

// Constants for Q8 repeater configuration
export const Q8_REPEATER_ID = '265';
export const Q8_COMPANY_FIELD_BASE = 'priorrealestatecompanyname245';
export const Q8_LICENSE_FIELD_BASE = 'priorrealestatelicensenumber246';

// Default keyword names if not found in fieldConfig
export const Q8_COMPANY_KEYWORD_NAME = 'Prior Real Estate Company Name';
export const Q8_LICENSE_KEYWORD_NAME = 'Prior Real Estate License Number';

// Languages (Spoken) repeater configuration
export const LANGUAGES_FIELD_ID = 'languagesspoken117';
export const LANGUAGES_REPEATER_ID = '253';
export const LANGUAGES_DEFAULT_KEYWORD_NAME = 'Languages Spoken';

// Q7 Licensure in Other States repeater configuration (single column of state values)
export const Q7_REPEATER_ID = '101';
export const Q7_FIELD_BASE = 'textbox258';
export const Q7_DEFAULT_KEYWORD_NAME = 'Licensure State';

export function buildQ7RepeaterKeywords(
  data: Record<string, unknown>,
  fieldConfig: unknown
): RepeaterEntry | null {
  const gateRaw = data['q7metro619240'];
  const isNo = typeof gateRaw === 'string' && /^no$/i.test(gateRaw.trim());
  if (isNo) {
    return { repeaterId: Q7_REPEATER_ID, repeaterKeywords: [] };
  }

  const rows: string[] = [];
  const baseVal = data[Q7_FIELD_BASE];
  if (typeof baseVal === 'string' && baseVal.trim()) rows.push(baseVal.trim());
  let i = 0;
  while (data[`${Q7_FIELD_BASE}_${i}`] !== undefined) {
    const v = data[`${Q7_FIELD_BASE}_${i}`];
    if (typeof v === 'string' && v.trim()) rows.push(v.trim());
    i++;
  }
  if (rows.length === 0) return null; // nothing present & not No gate

  const field = findFieldById(fieldConfig, Q7_FIELD_BASE) as (FieldConfigEntry & { keywordName?: string }) | undefined;
  const keywordName = field?.keywordName ?? Q7_DEFAULT_KEYWORD_NAME;
  const repeaterKeywords: RepeaterRow[] = rows.map(state => ({ [keywordName]: state }));
  return { repeaterId: Q7_REPEATER_ID, repeaterKeywords };
}

// (RepeaterRow & RepeaterEntry now sourced from ../types/domain)

/**
 * Extract Q8 field data from form data and convert to repeaterKeywords format
 * Handles both legacy format (priorrealestatecompanyname245) and indexed format (priorrealestatecompanyname245_0)
 * @param data - form data object containing Q8 fields
 * @param fieldConfig - field configuration to get proper keyword names
 * @returns RepeaterEntry for Q8 data or null if no Q8 data found
 */
export function buildQ8RepeaterKeywords(
  data: Record<string, unknown>,
  fieldConfig: unknown
): RepeaterEntry | null {
  // If Q8 gating question explicitly answered "No", we must still send an empty repeater structure
  // so that the backend (and local persistence) can clear any previously stored rows.
  const q8GateRaw = data['q8metro619240'];
  const q8IsNo = typeof q8GateRaw === 'string' && /^no$/i.test(q8GateRaw.trim());

  // When gate is "No" we bypass row extraction and return an empty repeater payload.
  if (q8IsNo) {
    return { repeaterId: Q8_REPEATER_ID, repeaterKeywords: [] };
  }

  // Find all Q8 fields - both legacy and indexed formats
  const companyFields: Record<number, string> = {};
  const licenseFields: Record<number, string> = {};
  Object.entries(data).forEach(([key, value]) => {
    if (typeof value !== 'string' || !value.trim()) return;
    
    // Check for legacy format first (index 0)
    if (key === Q8_COMPANY_FIELD_BASE) {
      companyFields[0] = value;
      return;
    }
    if (key === Q8_LICENSE_FIELD_BASE) {
      licenseFields[0] = value;
      return;
    }
    
    // Check for indexed company name fields
    const companyMatch = /^priorrealestatecompanyname245_(\d+)$/.exec(key);
    if (companyMatch) {
      const index = parseInt(companyMatch[1], 10);
      companyFields[index] = value;
      return;
    }
    
    // Check for indexed license number fields
    const licenseMatch = /^priorrealestatelicensenumber246_(\d+)$/.exec(key);
    if (licenseMatch) {
      const index = parseInt(licenseMatch[1], 10);
      licenseFields[index] = value;
    }
  });
  
  // Get all unique indices that have either company or license data
  const indices = new Set([...Object.keys(companyFields), ...Object.keys(licenseFields)].map(Number));
  
  if (indices.size === 0) {
    // No row data present (and gate not explicitly No) => do not include repeater
    return null;
  }
  
  // Get keyword names from fieldConfig
  const companyField = findFieldById(fieldConfig, Q8_COMPANY_FIELD_BASE);
  const licenseField = findFieldById(fieldConfig, Q8_LICENSE_FIELD_BASE);
  
  const companyKeywordName = companyField?.keywordName ?? Q8_COMPANY_KEYWORD_NAME;
  const licenseKeywordName = licenseField?.keywordName ?? Q8_LICENSE_KEYWORD_NAME;
  
  // Build repeater keywords array
  const repeaterKeywords: RepeaterRow[] = [];
  
  Array.from(indices)
    .sort((a, b) => a - b) // numeric ascending; avoids lexicographic ('10' before '2') issue
    .forEach(index => {
      const companyName = companyFields[index] ?? '';
      const licenseNumber = licenseFields[index] ?? '';
      
      // Include a row if EITHER field has a non-blank value. Previous implementation
      // used the nullish coalescing operator (companyName.trim() ?? licenseNumber.trim()) which
      // incorrectly skipped rows when companyName was an empty string and only licenseNumber existed.
      if (companyName.trim() || licenseNumber.trim()) {
        const keyword: RepeaterRow = {};
        
        if (companyName.trim()) {
          keyword[companyKeywordName] = companyName;
        }
        
        if (licenseNumber.trim()) {
          keyword[licenseKeywordName] = licenseNumber;
        }
        
        repeaterKeywords.push(keyword);
      }
    });
  return repeaterKeywords.length > 0 ? {
    repeaterId: Q8_REPEATER_ID,
    repeaterKeywords
  } : null;
}

/**
 * Parse repeaterKeywords back to indexed field format for form hydration
 * @param repeaterEntry - RepeaterEntry containing Q8 repeater data
 * @param fieldConfig - field configuration to validate keyword names
 * @returns Object with indexed field keys and values
 */
export function parseQ8RepeaterKeywords(
  repeaterEntry: RepeaterEntry,
  fieldConfig: unknown
): Record<string, string> {
  if (repeaterEntry.repeaterId !== Q8_REPEATER_ID) {
    return {};
  }
  
  // Get keyword names from fieldConfig
  const companyField = findFieldById(fieldConfig, Q8_COMPANY_FIELD_BASE);
  const licenseField = findFieldById(fieldConfig, Q8_LICENSE_FIELD_BASE);
  
  const companyKeywordName = companyField?.keywordName ?? Q8_COMPANY_KEYWORD_NAME;
  const licenseKeywordName = licenseField?.keywordName ?? Q8_LICENSE_KEYWORD_NAME;
  
  const result: Record<string, string> = {};
  
  repeaterEntry.repeaterKeywords.forEach((keyword, index) => {
    const companyName = keyword[companyKeywordName];
    const licenseNumber = keyword[licenseKeywordName];
    
    if (companyName) {
      result[`${Q8_COMPANY_FIELD_BASE}_${index}`] = companyName;
    }
    
    if (licenseNumber) {
      result[`${Q8_LICENSE_FIELD_BASE}_${index}`] = licenseNumber;
    }
  });
  
  return result;
}

/**
 * Check if form data contains Q8 indexed fields
 * @param data - form data object
 * @returns true if Q8 indexed fields are present
 */
export function hasQ8IndexedFields(data: Record<string, unknown>): boolean {
  return Object.keys(data).some(key => 
    key.startsWith('priorrealestatecompanyname245_') || 
    key.startsWith('priorrealestatelicensenumber246_')
  );
}

/**
 * Check if Q8 primary question is answered "Yes"
 * @param data - form data object
 * @returns true if Q8 is answered "Yes"
 */
export function isQ8EnabledInData(data: Record<string, unknown>): boolean {
  const q8Value = data['q8metro619240'];
  return typeof q8Value === 'string' && /^yes$/i.test(q8Value);
}

/**
 * Build Languages Spoken repeater (one row per language) from a comma-separated field.
 * @param data form data containing languagesspoken117
 * @param fieldConfig to resolve keywordName / custom repeaterId if provided
 * @returns RepeaterEntry or null
 */
export function buildLanguagesRepeaterKeywords(
  data: Record<string, unknown>,
  fieldConfig: unknown
): RepeaterEntry | null {
  const raw = data[LANGUAGES_FIELD_ID];
  if (typeof raw !== 'string') return null; // no field at all
  if (!raw.trim()) {
    // Explicitly present but blank string -> send empty repeater to clear prior rows
    const field = findFieldById(fieldConfig, LANGUAGES_FIELD_ID) as (FieldConfigEntry & { repeaterId?: string }) | undefined;
    // Coerce repeaterId to string at runtime to satisfy payload/localStorage contract
    const repeaterId = String((field?.repeaterId ?? LANGUAGES_REPEATER_ID));
    return { repeaterId, repeaterKeywords: [] };
  }
  const values = raw
    .split(',')
    .map(v => v.trim())
    .filter(Boolean);
  if (!values.length) return null;

  // Deduplicate while preserving order
  const seen = new Set<string>();
  const unique = values.filter(v => {
    const key = v.toLowerCase();
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });

  const field = findFieldById(fieldConfig, LANGUAGES_FIELD_ID) as (FieldConfigEntry & { repeaterId?: string }) | undefined;
  // Safely derive the keyword label; fall back if value is not a non-empty string
  const candidateLabel = field?.keywordName ?? field?.displayLabel;
  const keyLabel = (typeof candidateLabel === 'string' && candidateLabel.trim())
    ? candidateLabel
    : LANGUAGES_DEFAULT_KEYWORD_NAME;
  // Coerce repeaterId to string to guard against numeric values coming from field config
  const repeaterId = String(field?.repeaterId ?? LANGUAGES_REPEATER_ID);

  const repeaterKeywords: RepeaterRow[] = unique.map(lang => ({ [keyLabel]: lang }));
  return { repeaterId, repeaterKeywords };
}

export function parseQ7RepeaterKeywords(
  repeaterEntry: RepeaterEntry,
  fieldConfig: unknown
): Record<string, string> {
  if (repeaterEntry.repeaterId !== Q7_REPEATER_ID) return {};
  const rows = repeaterEntry.repeaterKeywords || [];
  if (!Array.isArray(rows) || rows.length === 0) return {};
  // Resolve keyword name used in payload
  const field = findFieldById(fieldConfig, Q7_FIELD_BASE) as (FieldConfigEntry & { keywordName?: string }) | undefined;
  const keywordName = field?.keywordName ?? Q7_DEFAULT_KEYWORD_NAME;
  const result: Record<string, string> = {};
  if (rows.length === 1) {
    const first = rows[0];
    const val = first && typeof first === 'object' ? (first as Record<string, string>)[keywordName] : undefined;
    if (typeof val === 'string' && val.trim()) {
      result[Q7_FIELD_BASE] = val.trim();
    }
    return result;
  }
  // Multiple rows -> indexed form
  rows.forEach((row, idx) => {
    if (!row || typeof row !== 'object') return;
    const val = (row as Record<string, unknown>)[keywordName];
    if (typeof val === 'string' && val.trim()) {
      result[`${Q7_FIELD_BASE}_${idx}`] = val.trim();
    }
  });
  return result;
}